/** Membership summary bar and its private manage/cancel satellites. */
import { useEffect, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getMembershipEntryPreflight,
  getSubscriptionPreflight,
  type MembershipEntryPreflight,
  type SubscriptionPreflight,
  type MemberMembership,
} from '@/api/members';
import { ApiError } from '@/api/errors';
import { v2ErrorCode } from '@/api/v2Errors';
import { serverMessage } from '@/api/serverMessage';
import {
  DEFAULT_MEMBERSHIP_CANCEL_MODE,
  cancelMembershipSubscription,
  pauseMembershipSubscription,
  resumeMembershipSubscription,
  type MembershipCancelMode,
} from '@/api/membershipSubscription';
import { useToast } from '@/components/useToast';
import {
  getMembershipNextCharge,
  type MembershipNextCharge,
} from '@/api/membershipNextCharge';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import { buildMembersMaps } from '@/theme/maps/members';
import { listMembershipPlans, type MembershipPlan } from '@/api/membershipPlans';
import { scrubBrand } from '@/utils/whitelabel';
import { dedupeCardBrand } from '@/utils/cardBrand';
import { formatApiDate } from '@/components/list/listDate';
import { shortDateLabel } from '@/utils/clockTime';
import { ChangePlanDialog } from './ChangePlanDialog';
import { NextChargeBillingBlock } from './NextChargeBillingBlock';
import { LinkSubscriptionDialog } from './LinkSubscriptionDialog';
import { PLACEHOLDER, statusChip } from './memberDetailShared';
import { MEMBERS_FORBIDDEN_MESSAGE, MEMBERS_LOAD_FAILED_MESSAGE } from './membersError';
import { ScreenError } from '@/components/ScreenError';

export function MembershipSummaryBar({
  membership,
  tier,
  status,
  membershipReady,
  loadError,
  onRetryLoad,
  onBuy,
  contactId,
  canManage = true,
  onSubscriptionChanged,
}: {
  membership: MemberMembership | null;
  tier: string | null;
  status: string | null;
  /**
   * The membership resource LOADED — either shape. An absent membership loads as an
   * all-null view (`200`, or a `404` the API layer resolves to the same), so this is
   * "we know the answer", never "there is a membership".
   */
  membershipReady: boolean;
  /**
   * The membership load FAILED (DNKADM-0333), or null. Deliberately separate from
   * `membershipReady`: a failure must render its own state, never the empty card, and
   * must NOT offer a Buy flow built on data we could not fetch.
   */
  loadError: unknown;
  /** Re-run the failed membership load — admin-triggered, never automatic. */
  onRetryLoad: () => void;
  /** Called with the plan the pre-flight just gated — the order is bound to it (DNKADM-0231). */
  onBuy: (planId: string) => void;
  contactId: string;
  /**
   * Buy membership, Pause/Resume, Change plan and Cancel (DNKADM-0375). Membership
   * changes are OWNER/ADMIN on the server, so a STAFF session reads the card without
   * them. Defaults to `true`.
   */
  canManage?: boolean;
  onSubscriptionChanged: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [actionState, setActionState] = useState<SubscriptionActionState>({ kind: 'idle' });
  const [buyBusy, setBuyBusy] = useState(false);
  const [buyError, setBuyError] = useState<string | null>(null);
  const [buyNotice, setBuyNotice] = useState<string | null>(null);
  const [cancelMode, setCancelMode] = useState<MembershipCancelMode>(
    DEFAULT_MEMBERSHIP_CANCEL_MODE,
  );
  const [planChoices, setPlanChoices] = useState<MembershipPlan[] | null>(null);
  const [selectedBuyPlanId, setSelectedBuyPlanId] = useState('');
  const [relinkSubscriptionId, setRelinkSubscriptionId] = useState<string | null>(null);
  const showToast = useToast();

  // DNKADM-0374: next-charge fetch. dink-api opens it to STAFF too (DNKADM-0375).
  // `nextChargeData: null` = loading/hidden; `MembershipNextCharge` = loaded.
  const [nextChargeData, setNextChargeData] = useState<MembershipNextCharge | null>(null);
  const membershipPlanId = membership?.membershipPlanId ?? null;
  const cancelScheduledAt = membership?.cancelScheduledAt ?? null;
  const pausedAt = membership?.pausedAt ?? null;
  const subscriptionStatusForNextCharge = normalizeSubscriptionStatus(
    membership?.subscriptionStatus,
  );

  useEffect(() => {
    if (membershipPlanId === null) {
      setNextChargeData(null);
      return;
    }
    let cancelled = false;
    setNextChargeData(null);
    void getMembershipNextCharge(contactId)
      .then((data) => {
        if (!cancelled) setNextChargeData(data);
      })
      .catch(() => {
        // 403, 404, network, malformed → hide the billing block; no toast.
        if (!cancelled) setNextChargeData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    contactId,
    membershipPlanId,
    subscriptionStatusForNextCharge,
    cancelScheduledAt,
    pausedAt,
  ]);
  // DNKADM-0333: three distinct states, not two. `loadFailed` is checked FIRST so a failed
  // load can never fall through to the loaded-empty rendering — the reported defect was
  // that they were the same empty card.
  const loadFailed = loadError !== null && loadError !== undefined;
  const loadedNoMembership =
    membershipReady && membership !== null && membership.membershipPlanId === null;
  const displayPlan =
    membershipReady && membership !== null
      ? loadedNoMembership
        ? PLACEHOLDER
        : (membership.membershipPlanName ?? tier ?? PLACEHOLDER)
      : (tier ?? PLACEHOLDER);
  const subscriptionStatus = normalizeSubscriptionStatus(membership?.subscriptionStatus);
  // `membership.membershipStatus` is where the server's `effectiveStatus` lands
  // (`parseMembership`). effectiveStatus is the ENTITLEMENT answer and the server derives it
  // FROM subscriptionStatus (dink-api ruling 2026-08-03): it gates the chip and the action set.
  // subscriptionStatus only fills in when the server sent no effective status, and otherwise
  // merely ENRICHES the display. Reading subscriptionStatus first was DNKADM-0242's defect: a
  // membership lapsed past its grace window while the subscription still bills reports
  // `subscriptionStatus: 'active'` + `effectiveStatus: 'Inactive'`, and rendered as Active.
  const displayStatus = loadedNoMembership
    ? null
    : (membership?.membershipStatus ?? membershipStatusLabel(subscriptionStatus) ?? status);
  const st = displayStatus !== null ? statusChip(displayStatus, theme.palette.mode) : null;
  const hasMembership =
    membershipReady &&
    membership?.membershipPlanId !== null &&
    membership?.membershipPlanId !== undefined;
  const displayKey = statusKey(displayStatus); // 'active' | 'paused' | 'inactive' | null | …
  // The subscriptionStatus clauses are equivalent-or-narrower per the ruling
  // (`effectiveStatus === 'Paused'` iff `subscriptionStatus === 'paused'`) and preserve the
  // behavior when the server sent no effectiveStatus. Paused wins over Active/Inactive.
  const isPaused = subscriptionStatus === 'paused' || displayKey === 'paused';
  const isInactive = subscriptionStatus === 'canceled' || displayKey === 'inactive';
  const isActiveMembership = hasMembership && !isPaused && !isInactive;
  // DISPLAY-ONLY enrichment (DNKADM-0242): `past_due` = still entitled, in grace, but a
  // renewal charge failed; `canceled` = the subscription is terminated. Neither touches an
  // action gate — the gates key off effectiveStatus alone.
  const isPastDue = subscriptionStatus === 'past_due';
  const isCanceledSubscription = subscriptionStatus === 'canceled';
  // An at-period-end cancel is already pending (DNKAPI-0397). The subscription is still
  // genuinely active until the boundary, so this is a separate field, not a status value.
  const isCancelScheduled = (membership?.cancelScheduledAt ?? null) !== null;
  // DNKADM-0353 U3: the word next to the date must be true for the membership's state — a
  // subscription that is actively billing does not "expire", it renews, and an operator
  // cannot otherwise tell whether that date belongs to the membership or a card. Cancelling
  // wins over Active (a scheduled cancel means it will NOT renew, whatever else is true of
  // it) — this reuses the SAME wording DNKADM-0242 already shipped for the cancel-scheduled
  // hint, so the primary label now carries it instead of a separate, redundant "· Cancels on"
  // segment. Paused, Inactive, or any other state keeps "Expiration": nothing there is
  // auto-renewing, so the date genuinely IS the one access lapses on if the membership is
  // never resumed or rebought (`expiration` is server doc'd as "a null expiration never
  // lapses" — it is not an active-billing-only concept).
  // DNKADM-0419: an immediate cancel (`canceled`, nothing scheduled) that is still inside its
  // paid period is effectively Active server-side — usable through expiration — but "Active"
  // beside "subscription canceled" read as a contradiction. Say when access ends instead.
  // DNKAPI-0600: the date is the server's `effectiveAccessEndsOn` (expiration + grace days, the
  // real last usable day); a backend predating it falls back to the expiration as sent.
  const accessEndsOn =
    membership?.effectiveAccessEndsOn ?? membership?.membershipExpiration ?? null;
  const isAccessEnding =
    hasMembership &&
    isCanceledSubscription &&
    !isCancelScheduled &&
    !isPaused &&
    displayKey === 'active' &&
    accessEndsOn !== null;
  const membershipDateLabel = isCancelScheduled
    ? 'Cancels on'
    : isActiveMembership
      ? 'Renews on'
      : isAccessEnding
        ? 'Access ends'
        : 'Expiration';
  // DNKADM-0353 U4: `maskedCard` is backend text (DNKAPI-0241 — the same white-label
  // convention `cardLabel` follows for the Payment methods tab) and is scrubbed at this
  // render boundary too, even though it is not itself a brand term today.
  // DNKADM-0355: unlike the Payment methods tab, nothing here prepends a brand —
  // `maskedCard` is composed upstream from pay-api's already brand-prefixed `maskedPan`
  // and ARRIVES doubled ("VISA VISA **** 1111"). The dedupe is the render-boundary guard
  // for that; it is a no-op the day pay-api stops prefixing.
  const cardLine = dedupeCardBrand(scrubBrand(membership?.maskedCard ?? null)) ?? PLACEHOLDER;
  const statusPalette = isPaused
    ? { label: 'Paused', ...members.billingPaused }
    : isAccessEnding && st !== null
      ? { ...st, label: `Active until ${shortDateLabel(accessEndsOn) ?? accessEndsOn}` }
      : st;
  // Pause is suppressed once a cancel is scheduled (there is nothing left to pause into), but
  // RESUME is not: a paused-and-scheduled membership must still be resumable (DNKADM-0242).
  const pauseResumeAction =
    isActiveMembership && !isCancelScheduled
      ? ({ kind: 'pause', label: 'Pause', icon: 'pause_circle' } as const)
      : isPaused
        ? ({ kind: 'resume', label: 'Resume', icon: 'play_circle' } as const)
        : null;
  // Cancel is available from Active or Paused (cancel-from-paused); never from an
  // Inactive/lapsed or absent membership. Unchanged when a cancel is scheduled — it becomes
  // the immediate-only escalation, which the backend allows from every state.
  const canCancel = hasMembership && (isActiveMembership || isPaused);
  // Change plan ONLY while the membership is Active — the FE hides it when Paused (a paused
  // membership shows only Resume + Cancel) or Inactive. This supersedes the DNKADM-0233 choice
  // to delegate the paused/inactive cases to the server (DNKADM-0236). A scheduled cancel also
  // hides it: the membership is on its way out (DNKADM-0242).
  const canChangePlan = isActiveMembership && !isCancelScheduled;
  const actionBusy = actionState.kind === 'submitting';
  const controlsBusy = actionBusy || buyBusy;

  async function runSubscriptionAction(action: SubscriptionActionKind) {
    if (actionBusy) return;
    setActionState({ kind: 'submitting', action });
    try {
      if (action === 'pause') await pauseMembershipSubscription(contactId);
      else if (action === 'resume') await resumeMembershipSubscription(contactId);
      else await cancelMembershipSubscription(contactId, cancelMode);
      setActionState({ kind: 'idle' });
      setConfirmCancelOpen(false);
      onSubscriptionChanged();
    } catch (error) {
      setActionState({
        kind: 'error',
        action,
        view: toSubscriptionActionErrorView(error, action, subscriptionStatus),
      });
    }
  }

  /**
   * The "Buy membership" entry (DNKADM-0232). The FIRST question is plan-agnostic —
   * "does this member already have a subscription?" — asked before any plan is
   * chosen, so an existing one is caught up front. Only `NONE` continues to plan
   * selection; every other decision (including a decision this build does not know)
   * routes elsewhere and NEVER opens Buy. The button stays busy for the whole chain,
   * plan selection and the plan-scoped gate included.
   */
  async function startBuyPreflight() {
    if (buyBusy) return;
    setBuyError(null);
    setBuyNotice(null);
    setBuyBusy(true);
    try {
      const entry = await getMembershipEntryPreflight(contactId);
      if (entry.decision === 'NONE') {
        await startPlanSelection();
        return;
      }
      handleEntryPreflight(entry);
    } catch (error) {
      setBuyError(
        serverMessage(error) ?? 'Could not check this member’s membership. Try again.',
      );
    } finally {
      setBuyBusy(false);
    }
  }

  /** Route a non-`NONE` entry decision. Never opens the Buy flow. */
  function handleEntryPreflight(entry: MembershipEntryPreflight) {
    switch (entry.decision) {
      case 'LINKED': {
        // Already a live membership: re-read so the bar swaps to the Manage actions.
        const tier = scrubBrand(entry.tier);
        setBuyNotice(
          tier === null
            ? 'This member already has a membership. Showing the management actions.'
            : `This member already has a membership (${tier}). Showing the management actions.`,
        );
        onSubscriptionChanged();
        return;
      }
      case 'ORPHAN':
        if (entry.subscriptionId !== null) {
          setPlanChoices(null);
          setRelinkSubscriptionId(entry.subscriptionId);
        } else {
          setBuyError('An existing subscription was found, but its subscription id was missing.');
        }
        return;
      case 'IN_PROGRESS':
        setBuyError('A membership purchase is already in progress. Retry shortly.');
        return;
      case 'NONE':
      case 'UNKNOWN':
        setBuyError('Could not check this member’s membership. Try again.');
        return;
    }
  }

  /** The shipped plan-selection path: single active plan auto-gates, several open the chooser. */
  async function startPlanSelection() {
    try {
      const plans = await listMembershipPlans({ status: 'active' });
      if (plans.length === 0) {
        setBuyError('No active membership plan is available to buy.');
        return;
      }
      if (plans.length === 1) {
        await runBuyPreflight(plans[0]!.id);
        return;
      }
      setPlanChoices(plans);
      setSelectedBuyPlanId(plans[0]?.id ?? '');
    } catch (error) {
      setBuyError(serverMessage(error) ?? 'Membership plans could not be loaded.');
    }
  }

  async function runBuyPreflight(planId: string) {
    setBuyError(null);
    setBuyBusy(true);
    try {
      const preflight = await getSubscriptionPreflight(contactId, planId);
      handlePreflight(preflight, planId);
    } catch (error) {
      setBuyError(serverMessage(error) ?? 'Could not verify whether this membership can be bought.');
    } finally {
      setBuyBusy(false);
    }
  }

  /**
   * Route the pre-flight decision. `planId` is the plan that was just gated — on PROCEED it
   * is bound to the order so the ordered plan can never differ from the pre-flighted one
   * (DNKADM-0231).
   */
  function handlePreflight(preflight: SubscriptionPreflight, planId: string) {
    switch (preflight.decision) {
      case 'PROCEED':
        setPlanChoices(null);
        onBuy(planId);
        return;
      case 'ALREADY_ACTIVE':
        setBuyError('This member already has this membership.');
        onSubscriptionChanged();
        return;
      // Distinct from ALREADY_ACTIVE: a live subscription on ANOTHER plan (dink-api B1). Both
      // block Buy, but they are different facts and keep their own copy — do not merge them.
      case 'HAS_ACTIVE_MEMBERSHIP':
        setBuyError('This member already has an active membership on another plan.');
        onSubscriptionChanged();
        return;
      case 'ORPHAN_RELINK':
        if (preflight.subscriptionId !== null) {
          setPlanChoices(null);
          setRelinkSubscriptionId(preflight.subscriptionId);
        } else {
          setBuyError('An existing subscription was found, but its subscription id was missing.');
        }
        return;
      case 'IN_PROGRESS':
        setBuyError('A membership purchase is already in progress. Retry shortly.');
        return;
      case 'UNKNOWN':
        setBuyError('Could not verify whether this membership can be bought. Try again.');
        return;
    }
  }

  const retryAction =
    actionState.kind === 'error' && actionState.view.retryable ? actionState.action : null;

  return (
    <>
      <Box
        data-testid="membership-summary-bar"
        sx={{
          p: { xs: '16px', md: '16px 18px' },
          borderRadius: '12px',
          border: `1px solid ${t.line}`,
          bgcolor: t.surface,
          minWidth: 0,
          // DNKADM-0341 U1: content-driven, not breakpoint-driven. The card is always a
          // wrapping row: the identity block claims a sane minimum (its flex-basis) and
          // grows; the actions keep their natural width and drop to their own line the
          // moment the two no longer fit — at ANY viewport, including the ~half-width the
          // bar gets beside the sell card on an iPad (1024/1366), where the old `md` flip
          // forced a row the content could not hold and the text ran into the buttons.
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 1.5, minWidth: 0, flex: '1 1 280px' }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              bgcolor: members.ledger.summaryBg,
              color: t.brand,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name="workspace_premium" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: inkAlpha(theme, 0.62) }}>
              Membership
            </Typography>
            {loadFailed ? (
              <MembershipLoadError error={loadError} onRetry={onRetryLoad} />
            ) : loadedNoMembership ? (
              <Typography sx={{ fontSize: 16, fontWeight: 600, mt: 0.25 }}>
                No active membership
              </Typography>
            ) : (
              <>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0, mt: 0.25 }}
                >
                  <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{displayPlan}</Typography>
                  {statusPalette && (
                    <Chip
                      size="small"
                      label={statusPalette.label}
                      sx={{
                        color: statusPalette.fg,
                        bgcolor: statusPalette.bg,
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: statusPalette.dot },
                      }}
                      icon={
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: statusPalette.dot,
                          }}
                        />
                      }
                    />
                  )}
                </Stack>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    mt: 0.5,
                    color: inkAlpha(theme, 0.62),
                  }}
                >
                  <Typography component="span" sx={{ fontSize: 13 }}>
                    {membershipDateLabel}{' '}
                    {formatApiDate(isAccessEnding ? accessEndsOn : membership?.membershipExpiration)}
                  </Typography>
                  <Typography component="span" aria-hidden="true" sx={{ fontSize: 13 }}>
                    ·
                  </Typography>
                  <Typography component="span" sx={{ fontSize: 13 }}>
                    {/* `cardLine` is scrubbed AND brand-deduped above — see its derivation. */}
                    Card {cardLine}
                  </Typography>
                  {/*
                    One subscription hint at a time, paused first (DNKADM-0242): paused is the
                    entitlement state, past-due/canceled only enrich it. Display only — no gate.
                  */}
                  {isPaused ? (
                    <>
                      <Typography component="span" aria-hidden="true" sx={{ fontSize: 13 }}>
                        ·
                      </Typography>
                      <Typography component="span" sx={{ fontSize: 13, color: t.warnDark }}>
                        paused {membership?.pausedAt ?? PLACEHOLDER} · membership credits frozen
                      </Typography>
                    </>
                  ) : isPastDue ? (
                    <>
                      <Typography component="span" aria-hidden="true" sx={{ fontSize: 13 }}>
                        ·
                      </Typography>
                      <Typography component="span" sx={{ fontSize: 13, color: t.warnDark }}>
                        payment past due · update card
                      </Typography>
                    </>
                  ) : isCanceledSubscription ? (
                    <>
                      <Typography component="span" aria-hidden="true" sx={{ fontSize: 13 }}>
                        ·
                      </Typography>
                      <Typography component="span" sx={{ fontSize: 13 }}>
                        subscription canceled
                      </Typography>
                    </>
                  ) : null}
                  {/*
                    DNKADM-0353 U3: a scheduled cancel used to ALSO render its own trailing
                    "· Cancels on {date}" segment here (DNKADM-0242 step 4), alongside the
                    primary label above still reading "Expiration {date}" for the same date —
                    two different words for one value. The primary label is now state-aware
                    (`membershipDateLabel`) and reads "Cancels on {date}" itself when a cancel is
                    scheduled, so the standalone segment would only repeat it; it is removed
                    rather than shown twice.
                  */}
                </Stack>
              </>
            )}
            {/* DNKADM-0374: next-charge billing block — only when membership is loaded and has a plan */}
            {/* DNKADM-0419: its "Cancelled" headline only repeats "subscription canceled" above. */}
            {hasMembership &&
              nextChargeData !== null &&
              nextChargeData.reason !== 'no_membership' &&
              !(isAccessEnding && nextChargeData.reason === 'canceled') && (
                <NextChargeBillingBlock data={nextChargeData} theme={theme} />
              )}

            {actionState.kind === 'error' && !confirmCancelOpen && (
              <Alert
                severity="error"
                role="alert"
                sx={{ mt: 1.25 }}
                action={
                  retryAction ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => void runSubscriptionAction(retryAction)}
                    >
                      Retry
                    </Button>
                  ) : undefined
                }
              >
                {actionState.view.message}
              </Alert>
            )}
            {buyError !== null && (
              <Alert severity="warning" role="alert" sx={{ mt: 1.25 }}>
                {buyError}
              </Alert>
            )}
            {buyNotice !== null && (
              <Alert severity="info" role="status" sx={{ mt: 1.25 }}>
                {buyNotice}
              </Alert>
            )}
          </Box>
        </Stack>
        {canManage && (
          <Stack
            direction="row"
            sx={{
              gap: 0.75,
              flexWrap: 'wrap',
              // Natural width, never squeezed into the identity block (the card's line-break is
              // decided on natural sizes, so the actions drop to their own line before either
              // block shrinks); when it wraps to its own line `space-between` on a single item
              // lands it at the start (stacked look).
              //
              // DNKADM-0343 U1: but the stack MUST be allowed to shrink to the card once it is
              // on its own line. 0341 shipped `flex: 0 0 auto`, which pins the stack at the
              // full width of Pause + Change plan + Cancel; whenever that exceeds the card's
              // content width (the ~half-width bar beside the sell card) the stack overflows
              // the border — rightward in Chrome, and in WebKit as `Cancel` re-wrapped BELOW
              // the card's bottom edge (the line height was measured before the clamp).
              // `flex-shrink: 1` + `minWidth: 0` + `maxWidth: 100%` cap it at the card so the
              // buttons wrap INSIDE the stack, inside the border, at every width.
              flex: '0 1 auto',
              minWidth: 0,
              maxWidth: '100%',
              justifyContent: 'flex-end',
            }}
          >
            {membershipReady && (!hasMembership || isInactive) && (
              <Button
                size="small"
                startIcon={
                  buyBusy ? <CircularProgress size={16} color="inherit" /> : <Icon name="workspace_premium" />
                }
                onClick={() => void startBuyPreflight()}
                disabled={!membershipReady || controlsBusy}
              >
                {buyBusy ? 'Checking...' : 'Buy membership'}
              </Button>
            )}
            {hasMembership && pauseResumeAction && (
              <Button
                size="small"
                startIcon={<Icon name={pauseResumeAction.icon} />}
                onClick={() => void runSubscriptionAction(pauseResumeAction.kind)}
                disabled={!membershipReady || controlsBusy}
              >
                {actionState.kind === 'submitting' && actionState.action === pauseResumeAction.kind
                  ? `${pauseResumeAction.label}…`
                  : pauseResumeAction.label}
              </Button>
            )}
            {canChangePlan && (
              <Button
                size="small"
                startIcon={<Icon name="swap_horiz" />}
                onClick={() => setChangePlanOpen(true)}
                disabled={!membershipReady || controlsBusy}
              >
                Change plan
              </Button>
            )}
            {canCancel && (
              <Button
                size="small"
                color="error"
                startIcon={<Icon name="cancel" />}
                onClick={() => {
                  setActionState({ kind: 'idle' });
                  // A scheduled cancel can ONLY be escalated: re-sending at_period_end is exactly
                  // the duplicate the backend now rejects with 409 (DNKAPI-0397).
                  setCancelMode(isCancelScheduled ? 'immediate' : DEFAULT_MEMBERSHIP_CANCEL_MODE);
                  setConfirmCancelOpen(true);
                }}
                disabled={!membershipReady || controlsBusy}
              >
                {isCancelScheduled ? 'Cancel immediately' : 'Cancel'}
              </Button>
            )}
          </Stack>
        )}
      </Box>

      {confirmCancelOpen && (
        <CancelMembershipDialog
          busy={actionState.kind === 'submitting' && actionState.action === 'cancel'}
          error={actionState.kind === 'error' ? actionState.view : null}
          mode={cancelMode}
          immediateOnly={isCancelScheduled}
          onModeChange={setCancelMode}
          onClose={() => {
            if (!actionBusy) setConfirmCancelOpen(false);
          }}
          onCancel={() => void runSubscriptionAction('cancel')}
          onRetry={() => void runSubscriptionAction('cancel')}
        />
      )}

      {changePlanOpen && (
        <ChangePlanDialog
          contactId={contactId}
          currentPlanId={membership?.membershipPlanId ?? null}
          currentPlanName={membership?.membershipPlanName ?? tier}
          open
          onClose={() => setChangePlanOpen(false)}
          onChanged={onSubscriptionChanged}
        />
      )}

      {planChoices !== null && (
        <Dialog open onClose={() => (!buyBusy ? setPlanChoices(null) : undefined)} maxWidth="xs" fullWidth>
          <DialogTitle>Choose membership</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Select the plan to verify before opening the Buy dialog.
              </Typography>
              <TextField
                label="Plan"
                select
                value={selectedBuyPlanId}
                onChange={(event) => setSelectedBuyPlanId(event.target.value)}
                fullWidth
              >
                {planChoices.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name}
                  </MenuItem>
                ))}
              </TextField>
              {buyError !== null && (
                <Alert severity="warning" role="alert">
                  {buyError}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPlanChoices(null)} disabled={buyBusy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={buyBusy || selectedBuyPlanId.length === 0}
              onClick={() => void runBuyPreflight(selectedBuyPlanId)}
            >
              {buyBusy ? 'Checking...' : 'Continue'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {relinkSubscriptionId !== null && (
        <LinkSubscriptionDialog
          contactId={contactId}
          subscriptionId={relinkSubscriptionId}
          open
          onClose={() => setRelinkSubscriptionId(null)}
          onLinked={() => {
            setRelinkSubscriptionId(null);
            showToast('Subscription linked.');
            onSubscriptionChanged();
          }}
        />
      )}
    </>
  );
}

/**
 * The membership read FAILED (DNKADM-0333) — the state that used to render as an empty
 * card with every action silently gone. It reuses the shared members error view so a
 * dink-api `403` keeps its dedicated "needs OWNER/ADMIN" copy rather than a generic line,
 * and the retry is admin-triggered (no auto-refetch loop). The card's actions stay hidden
 * while this shows: every one of them is gated on the load having succeeded.
 */
function MembershipLoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <ScreenError
      error={error}
      what="the membership"
      onRetry={onRetry}
      forbiddenMessage={MEMBERS_FORBIDDEN_MESSAGE}
      retryMessage={MEMBERS_LOAD_FAILED_MESSAGE}
      preferServerMessage
      sx={{ mt: 1 }}
    />
  );
}

type SubscriptionActionKind = 'pause' | 'resume' | 'cancel';

type SubscriptionActionState =
  | { kind: 'idle' }
  | { kind: 'submitting'; action: SubscriptionActionKind }
  | {
      kind: 'error';
      action: SubscriptionActionKind;
      view: SubscriptionActionErrorView;
    };

interface SubscriptionActionErrorView {
  message: string;
  retryable: boolean;
}

function normalizeSubscriptionStatus(value: string | null | undefined): string | null {
  if (value == null) return null;
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

function statusKey(value: string | null): string | null {
  return value?.toLowerCase().replace(/\s+/g, '_') ?? null;
}

function membershipStatusLabel(subscriptionStatus: string | null): string | null {
  switch (subscriptionStatus) {
    case 'active':
      return 'Active';
    case 'paused':
      return 'Paused';
    case 'canceled':
      return 'Inactive';
    default:
      return null;
  }
}

function toSubscriptionActionErrorView(
  error: unknown,
  action: SubscriptionActionKind,
  subscriptionStatus: string | null,
): SubscriptionActionErrorView {
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    if (error.status === 409) {
      // Branch on the envelope CODE, never the message text (DNKAPI-0397): a duplicate
      // at-period-end cancel is refused without calling the PSP, so it is a settled state,
      // not a transient failure — never retryable, and the escalation is the way out.
      const hint =
        v2ErrorCode(error) === 'CANCELLATION_ALREADY_SCHEDULED'
          ? ' Use “Cancel immediately” to end it now.'
          : action === 'pause' && subscriptionStatus === 'past_due'
            ? ' Resolve dunning or cancel the membership instead.'
            : '';
      return {
        retryable: false,
        message: (msg ?? 'This subscription action is not allowed in the current state.') + hint,
      };
    }
    if (error.status === 502 || error.retryable === true) {
      return {
        retryable: error.retryable === true,
        message: msg ?? 'Payment service unavailable. Try again.',
      };
    }
    if (error.code === 'FORBIDDEN') {
      return { retryable: false, message: 'You’re not authorized to update this membership.' };
    }
    if (error.code === 'SESSION_EXPIRED') {
      return {
        retryable: false,
        message: 'Your session expired. Please sign in again to continue.',
      };
    }
    if (msg) return { retryable: false, message: msg };
  }
  return { retryable: false, message: 'The subscription action could not be completed.' };
}

/**
 * The cancel effective-date choice, safe option first (DNKADM-0232) — the operator
 * picks, the FE never decides silently.
 */
const CANCEL_MODE_OPTIONS: ReadonlyArray<{ value: MembershipCancelMode; label: string }> = [
  {
    value: DEFAULT_MEMBERSHIP_CANCEL_MODE,
    label: 'Cancel at period end — the member keeps access until the current period ends',
  },
  { value: 'immediate', label: 'Cancel immediately — access ends now' },
];

function CancelMembershipDialog({
  busy,
  error,
  mode,
  immediateOnly,
  onModeChange,
  onClose,
  onCancel,
  onRetry,
}: {
  busy: boolean;
  error: SubscriptionActionErrorView | null;
  /** The operator-chosen effective date; defaults to at-period-end (DNKADM-0232). */
  mode: MembershipCancelMode;
  /**
   * A cancel is already scheduled (DNKAPI-0397): the only remaining move is escalating to
   * immediate, so the two-mode choice is withheld rather than offering the duplicate the
   * backend rejects.
   */
  immediateOnly: boolean;
  onModeChange: (mode: MembershipCancelMode) => void;
  onClose: () => void;
  onCancel: () => void;
  onRetry: () => void;
}) {
  const modeLabelId = `${useId()}-cancel-mode`;
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cancel membership</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <DialogContentText sx={{ fontSize: 14 }}>
            {immediateOnly
              ? 'This membership is already set to cancel at the end of the current period. Continuing ends it now — the member loses access immediately.'
              : 'This cancels the member’s current subscription. The membership will become inactive after the server confirms the cancellation.'}
          </DialogContentText>
          {!immediateOnly && (
            <FormControl disabled={busy}>
              <FormLabel id={modeLabelId} sx={{ fontSize: 13, fontWeight: 600 }}>
                When it takes effect
              </FormLabel>
              <RadioGroup
                aria-labelledby={modeLabelId}
                value={mode}
                onChange={(event) => onModeChange(event.target.value as MembershipCancelMode)}
              >
                {CANCEL_MODE_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
          {error && (
            <Alert
              severity="error"
              role="alert"
              action={
                error.retryable ? (
                  <Button color="inherit" size="small" onClick={onRetry}>
                    Retry
                  </Button>
                ) : undefined
              }
            >
              {error.message}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Close
        </Button>
        <Button variant="contained" color="error" onClick={onCancel} disabled={busy}>
          {busy ? 'Canceling…' : immediateOnly ? 'Cancel immediately' : 'Cancel membership'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

